/* eslint-disable import/no-named-as-default-member */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import { FormlyFieldConfig } from '@ngx-formly/core';
import { get } from 'lodash';
import * as moment from 'moment';
import { DateFormat } from '@app/constants';
import { isNil } from '.';

(moment as any).suppressDeprecationWarnings = true;

export const toDateFormat = (value: any, format: string): string => {
	let formatted = '';

	const date = moment.parseZone(value);
	if (value && date.isValid()) {
		formatted = date.format(format);
	}

	return formatted;
};

export const toShortDate = (value: any): string => toDateFormat(value, DateFormat.shortDate);

export const toFileDate = (value: any): string => toDateFormat(value, DateFormat.fileDate);

export const toDateRange = ({ start, end }: { start?: any; end?: any }): string => {
	const startRange = toShortDate(start);
	const endRange = toShortDate(end);

	if (startRange && endRange) {
		return `${startRange} - ${endRange}`;
	}

	if (startRange) {
		return `From ${startRange}`;
	}
	if (endRange) {
		return `To ${endRange}`;
	}

	return '';
};

export const booleanToLabel = (value: any, opts?: { true?: string; false?: string; allowAny?: boolean }): string => {
	const defaultOpts = { true: 'Yes', false: 'No', allowAny: true };
	const options = { ...defaultOpts, ...opts };

	if (!options.allowAny && typeof value !== 'boolean') {
		return '';
	}
	return value ? options.true : options.false;
};

export const activeToLabel = (value: any) => booleanToLabel(value, { true: 'Active', false: 'Inactive', allowAny: false });

export const toStartCase = (str: string): string =>
	str
		.replace(/_/g, ' ')
		.replace(/([a-z])([A-Z])/g, (_str, $1, $2) => `${$1} ${$2}`)
		.replace(/(\s|^)(\w)/g, (_str, $1, $2) => $1 + $2.toUpperCase());

export const applyAccountMask = (accountNumber: string, mask: string, applyMissingChars = true, missingChar = '?'): string => {
	if (isNil(mask) || mask.length === 0 || isNil(accountNumber) || accountNumber.length === 0) {
		return '';
	}

	let formattedValue = '';
	let cleanedValue = accountNumber.replace(/\./g, '');

	const chars = mask.split('');
	for (const char of chars) {
		if (cleanedValue.length > 0 || applyMissingChars) {
			if (char === '.') {
				formattedValue += '.';
			} else {
				formattedValue += cleanedValue.charAt(0).length > 0 ? cleanedValue.charAt(0) : missingChar;
				cleanedValue = cleanedValue.substring(1);
			}
		}
	}

	return formattedValue.toUpperCase();
};

export const precisionRound = (val: number, decimals = 2): number => {
	// eslint-disable-next-line @typescript-eslint/no-var-requires
	const stround = require('stround');
	if (decimals === null || decimals === undefined) {
		decimals = 2;
	}
	if (isNaN(val)) {
		return 0;
	}

	let strnum = val.toString();
	strnum = parseFloat(strnum).toFixed(decimals + 2);
	return Number(stround.round(strnum, decimals, stround.modes.HALF_UP));
};

/** Method to change a word into its plural form.
 * The English spelling rule is: when the word has a vowel (a, e, i, o, u) before the letter ‘y’, you add the letter ‘s’
 * and when the word has a consonant (b, c, d, f, g, h, j, k, l, m, n, p, q, r, s, t, v, w, x, y, z) before the
 * letter ‘y’, you remove the ‘y’ and replace it with ‘ies’.  If the word ends in s, ch, sh, or x you add the letters ‘es’. **/
export const singularToPlural = (item: string): string => {
	if (!item) {
		return '';
	}

	if (item.length < 2) {
		return `${item}s`;
	}

	const lastTwo = item.slice(-2);
	const singleEsMatches: string[] = ['x', 's'];
	const doubleEsMatches: string[] = ['ch', 'sh'];
	const vowels: string[] = ['a', 'e', 'i', 'o', 'u'];
	if (lastTwo[1].toLowerCase() === 'y' && vowels.indexOf(lastTwo[0]) === -1) {
		return `${item.substring(0, item.length - 1)}ies`;
	} else if (singleEsMatches.indexOf(lastTwo[1]) > -1 || doubleEsMatches.indexOf(lastTwo) > -1) {
		return `${item}es`;
	}

	return `${item}s`;
};

export const shortenTextIfApplicable = (value: string, allowedLength: number): string => {
	let addElipsis = false;
	const maxLines = 3;
	//Limit to maxLines of text
	if (value.split('\n').length >= maxLines) {
		value = value.split('\n').slice(0, maxLines).join('\n');
		addElipsis = true;
	}

	const maxCharacters = allowedLength;
	if (value.length > maxCharacters) {
		const limit = value.indexOf(' ') >= 0 ? value.substring(0, maxCharacters).lastIndexOf(' ') : maxCharacters;
		value = value.substring(0, limit);
		addElipsis = true;
	}
	if (addElipsis) {
		value += '...';
	}

	return value;
};

export const getFormattedNumber = (value: any, digits?: number): string => {
	if (digits === null || digits === undefined) {
		digits = 2;
	}
	const numValue = isNaN(parseFloat(value)) ? 0 : parseFloat(value.toString().replace(/,/g, ''));

	return numValue.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

export const formatAsCurrency = (value: any): string => {
	if (isNil(value) || isNaN(value)) {
		value = 0;
	}

	const formatter = new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
	});

	return formatter.format(value);
};

/**
 * Parses a currency string or number into a numeric value.
 * @param value The currency value to parse.
 * @returns The numeric representation of the currency value, or undefined if invalid.
 */
export const parseCurrencyToNumber = (value: string | number | null | undefined): number => {
	if (value === null || value === undefined || value === '') {
		return undefined;
	}

	// If already a number, return it
	if (typeof value === 'number') {
		return value;
	}

	// Remove currency symbols, commas, and extra spaces
	const cleanedValue = value
		.toString()
		.replace(/[$,\s]/g, '') // Remove $, commas, spaces
		.trim();

	// Handle empty string after cleaning
	if (cleanedValue === '') {
		return undefined;
	}

	const parsed = parseFloat(cleanedValue);
	return isNaN(parsed) ? undefined : parsed;
};

/**
 * Can be used in conjunction with blur on the formly select component marked as multiple = true.
 * Manages the Select dropdown checkboxes when All is selected or not.
 *
 * @param field
 * @param selections
 * @param allValue
 */
export const manageAllForMultiSelect = (field: FormlyFieldConfig, selections: any[], allValue: string | number): void => {
	setTimeout(() => {
		const noAllAvailableCount = (field.props.options as any[]).filter((s) => s.value !== allValue).length;
		const fControl = field.form.get(field.key.toString());
		const currSelections = fControl.value as any[];
		const currHasAll = currSelections.findIndex((s) => s === allValue) !== -1;
		const prevHasAll = selections.findIndex((s) => s === allValue) !== -1;
		const noAllCurrentCount = currSelections.filter((s) => s !== allValue).length;
		if (field.focus) {
			if (noAllAvailableCount === noAllCurrentCount) {
				if (!currHasAll && prevHasAll) {
					fControl.setValue([]);
				} else {
					currSelections.splice(0, 0, allValue);
					fControl.setValue(currSelections);
				}
			} else {
				if (currHasAll && !prevHasAll) {
					fControl.setValue((field.props.options as any[]).map((m) => m.value));
				} else {
					const allRemoved = currSelections.filter((s) => s !== allValue);
					fControl.setValue(allRemoved);
				}
			}
		} else if (currHasAll) {
			fControl.setValue([allValue]);
		}
	}, 200);
};

/**
 * Used in conjunction with formly type decimal components.
 * Applys decimal format if needed to numbers after blur or onInit etc.
 *
 * @param field
 * @param decimalPlaces
 * @param delayValue
 * @param blnEmitEvent
 */
export const applyDecimalFormat = (field: FormlyFieldConfig, decimalPlaces = 2, delayValue = 0, blnEmitEvent = false): void => {
	const FORM_CONTROL_VALUE = 'formControl.value';
	setTimeout(() => {
		const fieldControl = field.form.get(field.key.toString());
		if (fieldControl !== null && fieldControl.value !== getFormattedNumber(get(field, FORM_CONTROL_VALUE, 0), decimalPlaces)) {
			fieldControl.setValue(getFormattedNumber(get(field, FORM_CONTROL_VALUE, 0), decimalPlaces), { emitEvent: blnEmitEvent });
		}
	}, delayValue);
};

export const toQuestionMask = (accountMask: string): string => accountMask.replace(/0/g, '?');

export const pascalCaseAddSpaces = (strColumn: string): string => strColumn.replace(/([a-z])([A-Z])/g, '$1 $2');
